import { t } from '@lingui/core/macro';
import { BookingRecordDetailsCustomer } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton, noddiToast } from 'noddi-ui-web';
import { useAuthContext } from 'noddi-web';
import { useState } from 'react';
import { CustomerLoginModal } from '../Login/CustomerLoginModal';
import { MapPinSelectionDialog } from '../Map/MapPinSelectionDialog';

interface Props {
  booking: BookingRecordDetailsCustomer;
}

export const PatchMapPinLocationButton = ({ booking }: Props) => {
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const [hasTriggeredPinLocation, setHasTriggeredPinLocation] = useState(false);
  const { isLoggedIn } = useAuthContext();

  const { mutateAsync: patchBooking, isPending: isUpdatingLocation } = noddiAsync.usePatch({
    type: URLKeys.patchBooking,
    queryConfig: {
      onSuccess: async () => {
        await invalidateQueryExactMatch({
          urlKey: URLKeys.getBookingInfo,
          input: { slug: booking.slug }
        });
        setIsLocationPickerOpen(false);
        noddiToast.success(t`Pin location updated successfully`);
      }
    }
  });

  const pinLocationLatitude = booking.userGroupAddress?.pinLocationLatitude;
  const pinLocationLongitude = booking.userGroupAddress?.pinLocationLongitude;

  const initialLocationLatitude = pinLocationLatitude ?? booking.address.latitude;
  const initialLocationLongitude = pinLocationLongitude ?? booking.address.longitude;
  const hasPinLocation = !!pinLocationLatitude && !!pinLocationLongitude;

  function handlePinLocationClick() {
    if (isLoggedIn) {
      setIsLocationPickerOpen(true);
      return;
    }

    setHasTriggeredPinLocation(true);
  }

  return (
    <>
      {/* eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate glyph inside a customer-facing copy string, where a NoddiIcon element cannot be embedded */}
      <div className='-ml-1 flex items-center'>
        📍
        <NoddiButton
          size='small'
          variant='link'
          className='pl-2'
          loading={isUpdatingLocation}
          onClick={handlePinLocationClick}
        >
          {hasPinLocation ? t`Edit marker` : t`Mark where car is parked`}
        </NoddiButton>
      </div>

      <MapPinSelectionDialog
        isOpen={isLocationPickerOpen}
        initialLatitude={initialLocationLatitude}
        initialLongitude={initialLocationLongitude}
        addressLatitude={booking.address.latitude}
        addressLongitude={booking.address.longitude}
        isLoading={isUpdatingLocation}
        onLocationSelected={async (lat, lng) => {
          await patchBooking({
            bookingId: booking.id,
            pinLocationLatitude: lat,
            pinLocationLongitude: lng
          });
        }}
        onClose={() => setIsLocationPickerOpen(false)}
      />

      <CustomerLoginModal
        open={hasTriggeredPinLocation}
        onClose={() => setHasTriggeredPinLocation(false)}
        title={t`Sign in`}
        description={t`Please sign in to update the pin location`}
        onLoginSuccess={() => {
          setHasTriggeredPinLocation(false);
          setIsLocationPickerOpen(true);
        }}
      />
    </>
  );
};
